/**
 * Where a prompt came from, in words.
 *
 * A brand's prompt list is assembled by four different things at once — brand setup generates a few
 * dozen, the suggestion flow adds more, a person types one in, an agent adds one over MCP — and the
 * list showed no trace of which was which. The first question worth answering about a prompt that
 * measures the wrong thing is whether you wrote it.
 *
 * Values are the camelCase strings the API sends for PromptSource. Never compare against a number.
 */

export const PROMPT_SOURCES = {
  brandSetup: {
    label: "BrandKarma, during brand setup",
    short: "Generated",
    icon: "robot",
    color: "gray",
  },
  suggested: {
    label: "BrandKarma's suggestions, kept by",
    short: "Suggested",
    icon: "bulb",
    color: "yellow",
    /** The text is ours, the decision to keep it was a person's. Both halves matter. */
    namesAPerson: true,
  },
  manual: {
    label: "Written by hand by",
    short: "By hand",
    icon: "user",
    color: "blue",
    namesAPerson: true,
  },
  mcp: {
    label: "An agent over MCP, on behalf of",
    short: "Agent (MCP)",
    icon: "plug",
    color: "grape",
    namesAPerson: true,
  },
};

/**
 * Everything known about how a prompt came to exist, or null if there is nothing honest to say.
 *
 * Prompts created before provenance was recorded have no source. Rather than guessing, this infers
 * only what is provable: `queryTemplateId` is set exclusively by the inference service, so a legacy
 * prompt that has one was definitely generated. One without one stays unknown — which is the true
 * answer, and better than a confident wrong one on a list someone is about to edit from.
 */
export function promptProvenance(prompt) {
  if (!prompt) return null;

  const known = PROMPT_SOURCES[prompt.source];
  const created = prompt.idCreated ? new Date(prompt.idCreated) : null;

  if (known) {
    return {
      ...known,
      who: known.namesAPerson ? prompt.createdByName || "a team member" : null,
      created,
      sentence: sentence(known, prompt.createdByName, created),
    };
  }

  if (prompt.queryTemplateId) {
    const inferred = {
      label: "BrandKarma, from a standard template",
      short: "Generated",
      icon: "robot",
      color: "gray",
    };
    return {
      ...inferred,
      who: null,
      created,
      sentence: sentence(inferred, null, created),
    };
  }

  const unknown = {
    label: "Created before we started recording this",
    short: "Unknown",
    icon: "question",
    color: "gray",
  };
  return { ...unknown, who: null, created, sentence: sentence(unknown, null, created) };
}

function sentence(meta, name, created) {
  const who = meta.namesAPerson ? ` ${name || "a team member"}` : "";
  const when = created
    ? ` on ${created.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })}`
    : "";
  return `${meta.label}${who}${when}`;
}
